const PAGE_WIDTH = 595.28;
const PAGE_HEIGHT = 841.89;
const MARGIN_X = 50;
const MARGIN_TOP = 60;
const MARGIN_BOTTOM = 50;
const FONT_SIZE_BODY = 12;
const LINE_HEIGHT = 16;
const MAX_CHARS_PER_LINE = 88;

const WIN_1252_MAP: Record<number, number> = {
  0x20ac: 0x80,
  0x201a: 0x82,
  0x0192: 0x83,
  0x201e: 0x84,
  0x2026: 0x85,
  0x2020: 0x86,
  0x2021: 0x87,
  0x02c6: 0x88,
  0x2030: 0x89,
  0x0160: 0x8a,
  0x2039: 0x8b,
  0x0152: 0x8c,
  0x017d: 0x8e,
  0x2018: 0x91,
  0x2019: 0x92,
  0x201c: 0x93,
  0x201d: 0x94,
  0x2022: 0x95,
  0x2013: 0x96,
  0x2014: 0x97,
  0x02dc: 0x98,
  0x2122: 0x99,
  0x0161: 0x9a,
  0x203a: 0x9b,
  0x0153: 0x9c,
  0x017e: 0x9e,
  0x0178: 0x9f,
};

const sanitizeFileName = (value: string) => {
  const normalized = value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9_-]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');

  return normalized || 'certidao';
};

const toWin1252Hex = (value: string) => {
  let hex = '';

  for (const char of value) {
    const codePoint = char.codePointAt(0) ?? 0x3f;
    let byte = codePoint;

    if (codePoint > 0xff) {
      byte = WIN_1252_MAP[codePoint] ?? 0x3f;
    }

    hex += byte.toString(16).padStart(2, '0').toUpperCase();
  }

  return hex;
};

type PdfLine = {
  text: string;
  justify: boolean;
};

const wrapLine = (line: string, maxChars: number): string[] => {
  const trimmed = line.trim();
  if (!trimmed) return [''];

  const words = trimmed.split(/\s+/);
  const lines: string[] = [];
  let current = '';

  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (candidate.length <= maxChars) {
      current = candidate;
      continue;
    }

    if (current) {
      lines.push(current);
      current = word;
      continue;
    }

    let remaining = word;
    while (remaining.length > maxChars) {
      lines.push(remaining.slice(0, maxChars));
      remaining = remaining.slice(maxChars);
    }
    current = remaining;
  }

  if (current) {
    lines.push(current);
  }

  return lines;
};

const buildPdfTextLines = (body: string): PdfLine[] => {
  const lines: PdfLine[] = [];
  const paragraphs = body.replace(/\r\n/g, '\n').split('\n');

  for (const paragraph of paragraphs) {
    if (!paragraph.trim()) {
      lines.push({ text: '', justify: false });
      continue;
    }

    const wrapped = wrapLine(paragraph, MAX_CHARS_PER_LINE);
    wrapped.forEach((line, index) => {
      lines.push({
        text: line,
        justify: index < wrapped.length - 1 && line.includes(' '),
      });
    });
  }

  while (lines.length > 0 && !lines[lines.length - 1].text.trim()) {
    lines.pop();
  }

  return lines;
};

const paginateLines = (lines: PdfLine[]): PdfLine[][] => {
  const usableHeight = PAGE_HEIGHT - MARGIN_TOP - MARGIN_BOTTOM;
  const maxLinesFirstPage = Math.floor((usableHeight - LINE_HEIGHT) / LINE_HEIGHT);
  const maxLinesOtherPages = Math.floor(usableHeight / LINE_HEIGHT);
  const pages: PdfLine[][] = [];
  let index = 0;

  while (index < lines.length) {
    const isFirstPage = pages.length === 0;
    const pageSize = isFirstPage ? Math.max(1, maxLinesFirstPage) : Math.max(1, maxLinesOtherPages);
    pages.push(lines.slice(index, index + pageSize));
    index += pageSize;
  }

  return pages.length ? pages : [[{ text: '', justify: false }]];
};

const estimateLineWidth = (line: string, fontSize: number) => line.length * fontSize * 0.52;

const createPageContent = (lines: PdfLine[]): string => {
  const commands: string[] = ['BT', `/F1 ${FONT_SIZE_BODY} Tf`, `${MARGIN_X} ${PAGE_HEIGHT - MARGIN_TOP} Td`, `${LINE_HEIGHT} TL`];

  for (const line of lines) {
    if (!line.text.trim()) {
      commands.push('T*');
      continue;
    }

    if (line.justify) {
      const spaces = line.text.split(' ').length - 1;
      const availableWidth = PAGE_WIDTH - (MARGIN_X * 2);
      const textWidth = estimateLineWidth(line.text, FONT_SIZE_BODY);
      const extraSpace = Math.max(0, availableWidth - textWidth);
      const wordSpacing = spaces > 0 ? extraSpace / spaces : 0;

      commands.push(`${wordSpacing.toFixed(2)} Tw`);
      commands.push(`<${toWin1252Hex(line.text)}> Tj`);
      commands.push('0 Tw');
    } else {
      commands.push(`<${toWin1252Hex(line.text)}> Tj`);
    }

    commands.push('T*');
  }

  commands.push('ET');
  return commands.join('\n');
};

export const createCertificatePdfBlob = (body: string, _title = 'Certidao'): Blob => {
  const allLines = buildPdfTextLines(body);
  const pages = paginateLines(allLines);
  const objects: string[] = [];
  const pageObjectIds: number[] = [];
  const contentObjectIds: number[] = [];
  const fontObjectId = 3 + pages.length * 2;

  objects[0] = '1 0 obj\n<< /Type /Catalog /Pages 2 0 R >>\nendobj';

  for (let pageIndex = 0; pageIndex < pages.length; pageIndex += 1) {
    const pageObjectId = 3 + pageIndex * 2;
    const contentObjectId = pageObjectId + 1;
    pageObjectIds.push(pageObjectId);
    contentObjectIds.push(contentObjectId);
  }

  objects[1] = `2 0 obj\n<< /Type /Pages /Count ${pages.length} /Kids [${pageObjectIds.map((id) => `${id} 0 R`).join(' ')}] >>\nendobj`;

  pages.forEach((pageLines, pageIndex) => {
    const pageObjectId = pageObjectIds[pageIndex];
    const contentObjectId = contentObjectIds[pageIndex];
    const content = createPageContent(pageLines);

    objects[pageObjectId - 1] =
      `${pageObjectId} 0 obj\n<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${PAGE_WIDTH} ${PAGE_HEIGHT}] /Resources << /Font << /F1 ${fontObjectId} 0 R >> >> /Contents ${contentObjectId} 0 R >>\nendobj`;
    objects[contentObjectId - 1] =
      `${contentObjectId} 0 obj\n<< /Length ${content.length} >>\nstream\n${content}\nendstream\nendobj`;
  });

  objects[fontObjectId - 1] = `${fontObjectId} 0 obj\n<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>\nendobj`;

  let pdf = '%PDF-1.4\n';
  const offsets: number[] = [0];

  objects.forEach((object) => {
    offsets.push(pdf.length);
    pdf += `${object}\n`;
  });

  const xrefOffset = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n`;
  pdf += '0000000000 65535 f \n';

  for (let i = 1; i < offsets.length; i += 1) {
    pdf += `${offsets[i].toString().padStart(10, '0')} 00000 n \n`;
  }

  pdf += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;

  return new Blob([pdf], { type: 'application/pdf' });
};

export const buildCertificateFileBaseName = (recipientName?: string, _title?: string) => {
  const recipientSegment = sanitizeFileName(recipientName || 'destinatario');
  return `${recipientSegment}_${new Date().toISOString().slice(0, 10)}`;
};

export const buildCertificatePdfFileName = (recipientName?: string) =>
  `${buildCertificateFileBaseName(recipientName)}.pdf`;
